// Copyright (c) Ping. All rights reserved.
//
// Tiny URL helper for the chat sub-route `/chat/<id>` so a deep link
// or a back/forward navigation lands the user in the right chat
// without a router library.
//
// Why a sibling hook rather than extending RouterContext: the
// top-level router (`apps/web/src/shell/RouterContext.tsx`) models
// `route` as a closed string-literal union of WebRoute. Adding a
// per-route sub-id would require widening that type + plumbing it
// through MainContent + every host's read site. This hook stays
// confined to the chat surface and composes cleanly with the
// existing router — its `popstate` listener already resolves any
// unknown path back to `/chat`, so `/chat/<id>` doesn't disturb the
// outer route state.
//
// URL contract:
//   /chat                       → no selection (chat list, empty pane)
//   /chat/<id>                  → select conversation matching <id>;
//                                 <id> is preferred as a peer
//                                 account_id (direct chat key), with
//                                 a fallback to the local conversation
//                                 id (group chats / catchup state).
//   /chat/<id>/...trailing      → tolerated; trailing segments are
//                                 ignored so future extensions
//                                 (search params, message anchors)
//                                 don't break this matcher.
//
// History contract: `pushChatRouteId` always writes a
// `{ route: '/chat', chatId }` state so the outer router's
// popstate handler resolves the route back to '/chat' (it checks
// `popped.route` first via `isWebRoute`). Without that, popstate
// would fall back to `window.location.pathname` (e.g. `/chat/abc`)
// which fails `isWebRoute` and lands on the default '/chat' — same
// end state, but the `popped.route` branch is the documented path.

import { useEffect, useState } from 'react';

/**
 * Maximum length we'll treat as a valid id segment. Real ids are
 * 16-byte base64url (22 chars) for account_id, or 32 hex chars for
 * MLS conversation ids — both well under 128. The cap is purely a
 * defence against unbounded URL inputs ending up in setState.
 */
const MAX_ID_LEN = 128;

/**
 * Allowed id characters. Base64url (`A-Z`, `a-z`, `0-9`, `-`, `_`),
 * plus hex digits which are a subset. Anything else (slashes,
 * percent-encodings, dots) is treated as "no valid id".
 */
const ID_RE = /^[A-Za-z0-9_-]+$/;

/**
 * Extract the chat-route id from a pathname. Returns the trimmed
 * id when the URL matches `/chat/<id>` (with optional trailing
 * segments); returns null for `/chat`, `/chat/`, the empty string,
 * or any pathname that doesn't start with `/chat`.
 */
export function parseChatRouteId(pathname: string): string | null {
  if (!pathname.startsWith('/chat')) return null;
  // Strip the `/chat` prefix; the remainder is either '' or
  // starts with '/'. Tolerate the trailing-slash variant
  // (`/chat/`) as "no id".
  const rest = pathname.slice('/chat'.length);
  if (rest.length === 0 || rest === '/') return null;
  if (rest.charAt(0) !== '/') return null;
  // First path segment after `/chat/` is the id; ignore any
  // further trailing segments so forward-compatible extensions
  // (`/chat/<id>/messages/<seq>`, etc.) still resolve to the
  // right chat.
  const firstSlashAfter = rest.indexOf('/', 1);
  const segment = firstSlashAfter === -1 ? rest.slice(1) : rest.slice(1, firstSlashAfter);
  const decoded = (() => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  })();
  if (decoded.length === 0 || decoded.length > MAX_ID_LEN) return null;
  if (!ID_RE.test(decoded)) return null;
  return decoded;
}

/**
 * Push a new chat sub-route URL. Pass `null` to navigate back to
 * `/chat` with no selection.
 */
export function pushChatRouteId(id: string | null): void {
  if (typeof window === 'undefined') return;
  const nextPath = id === null ? '/chat' : `/chat/${encodeURIComponent(id)}`;
  // Skip the write when the URL would not change — avoids growing
  // the history stack on idempotent selects (StrictMode double-
  // mount, re-render with stable id, etc.).
  if (window.location.pathname === nextPath) return;
  // Mirror the outer router's state shape so its popstate listener
  // resolves the route back to '/chat' via the `popped.route` branch.
  window.history.pushState({ route: '/chat', chatId: id }, '', nextPath);
}

/**
 * Navigate to a chat route from OUTSIDE the `useChatRouteId` hook (e.g. the
 * profile overlays). `pushChatRouteId` only writes `history.pushState`, which
 * does NOT fire `popstate`, so a bare push updates the URL but never re-renders
 * `ChatRouteHost` (its `useChatRouteId` only re-reads on `popstate`). Dispatching
 * a synthetic `popstate` makes the hook pick up the new id and select the chat.
 */
export function navigateChatRouteId(id: string | null): void {
  pushChatRouteId(id);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('popstate'));
  }
}

/**
 * React hook: returns the current chat-route id and a setter that
 * pushes the URL + updates state. Subscribes to `popstate` so back/
 * forward navigations re-render the host with the right selection.
 */
export function useChatRouteId(): [string | null, (next: string | null) => void] {
  const [id, setId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : parseChatRouteId(window.location.pathname),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = (): void => {
      const next = parseChatRouteId(window.location.pathname);
      setId(next);
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const setter = (next: string | null): void => {
    pushChatRouteId(next);
    setId(next);
  };

  return [id, setter];
}
